import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, inject, ViewChild } from '@angular/core';
import { LetDirective } from '@ngrx/component';
import { TranslateModule } from '@ngx-translate/core';
import { CmsFacade } from '@rd/cms/facades/cms.facade';
import { UserCustomizationsFacade } from '@rd/core/facades/user-customizations-facade/user-customizations.facade';
import { DialogService } from '@rd/popup/services/dialog/dialog.service';
import { IconModule } from '@rd/shared-legacy/components/icon/icon.module';
import { CampaignBanner, CmsPageContentType } from '@rd/types';
import { isNotNull } from '@rd/utils/isNotNull';
import {
  EmblaCarouselDirective,
  EmblaEventType,
  EmblaOptionsType,
} from 'embla-carousel-angular';
import { filter, Observable } from 'rxjs';
import { BnzButtonModule } from '../../../shared/components/bnz-button/button.module';

@Component({
  selector: 'bnz-bankwide-introduction-dialog',
  template: `
    <div class="flex flex-col relative max-h-[calc(100vh-48px)] py-10 pt-11 px-5 lg:px-10">
      <button
        type="button"
        class="close-button button-reset focus-link absolute top-4 right-5 h-3 w-3 lg:right-4"
        [attr.aria-label]="'core.auth_dialog.close' | translate"
        (click)="closeDialog()">
        <rd-shared-icon
          class="h-3 w-3"
          [iconSymbol]="'icon-close'"
        >
        </rd-shared-icon>
      </button>

      <div class="flex-1 h-full overflow-auto">
        <div
          class="carousel-root overflow-hidden"
          emblaCarousel
          [options]="options"
          [subscribeToEvents]="subscribeToEvents"
          (emblaChange)="onEmblaChange()">
          <div class="carousel-container flex w-full h-[204px]">
            <div
              class="carousel-items flex-[0_0_100%] overflow-hidden flex items-center"
              *ngFor="
                let bannerItem of (bannerContent$ | async)?.items;
                let i = index
              "
              [id]="i">
              <img
                class="w-full hidden lg:block"
                [src]="bannerItem.desktopImageUrl"
                alt="" />
              <img
                class="w-full lg:hidden"
                [src]="bannerItem.mobileImageUrl"
                alt="" />
            </div>
          </div>

          <div
            *ngIf="(bannerContent$ | async)?.items as items"
            class="mt-5 mb-0 flex flex-col gap-y-5 lg:mt-6 lg:mb-6">
            <h3 class="my-0">{{ items[scrollIndex()]?.title }}</h3>
            <p class="my-0" [innerHTML]="items[scrollIndex()]?.mainContent"></p>
          </div>
        </div>
      </div>

      <div class="flex flex-col items-center justify-between gap-y-4 lg:flex-row">
        <div class="lg:hidden block mb-1">
          <ng-container *ngTemplateOutlet="slidesIndicator" />
        </div>

        <button
          bnz-button
          rdType="secondary"
          [ngClass]="[
            'w-full lg:w-[206px]',
            !scrollIndex() ? 'hidden lg:invisible lg:block': ''
          ]"
          (click)="onPrev()">
          {{ 'wl.bnz.home_page.bank_wide.dialog.back' | translate }}
        </button>

        <div class="hidden lg:block">
          <ng-container *ngTemplateOutlet="slidesIndicator" />
        </div>

        <ng-container *ngrxLet="(bannerContent$ | async)?.items as items">
          <ng-container *ngIf="!isLastSlide(items); else lastSlideContent">
            <button
              *ngIf="nextSlideTitle(items) as nextSlideTitle"
              bnz-button
              rdType="primary"
              class="w-full lg:w-[206px]"
              (click)="onNext()"
            >
              {{ 'wl.bnz.home_page.bank_wide.dialog.next' | translate: {
                nextSlideTitle
              } }}
            </button>
          </ng-container>

          <ng-template #lastSlideContent>
            <button
              bnz-button
              rdType="primary"
              class="w-full lg:w-[206px]"
              (click)="onLastSlideSelect()"
            >
              {{ 'wl.bnz.home_page.bank_wide.dialog.get_started' | translate }}
            </button>
          </ng-template>
        </ng-container>
      </div>
    </div>

    <ng-template #slidesIndicator>
      <div class="flex flex-row items-center justify-center gap-x-1 mt-5 lg:mt-0">
        <div
          *ngFor="let item of (bannerContent$ | async)?.items; index as i; trackBy: trackByFn"
          [ngClass]="[
            'transition-all duration-300 ease-in h-1.5 w-1.5 rounded-full',
            i === scrollIndex() ? '!w-3 rounded bg-[#007dc3]' : 'bg-[#d4d4d4]',
          ]">
        </div>
      </div>
    </ng-template>
  `,
  imports: [
    CommonModule,
    LetDirective,
    BnzButtonModule,
    EmblaCarouselDirective,
    TranslateModule,
    IconModule,
  ],
})
export class BnzBankWideIntroductionDialogComponent {
  @ViewChild(EmblaCarouselDirective) emblaRef: EmblaCarouselDirective;

  public readonly options: EmblaOptionsType = {
    watchDrag: false,
  };
  public readonly subscribeToEvents: EmblaEventType[] = ['select'];

  private cmsFacade = inject(CmsFacade);
  private dialogService = inject(DialogService);
  private userCustomizationsFacade = inject(UserCustomizationsFacade);
  private cdRef = inject(ChangeDetectorRef);

  bannerContent$: Observable<CmsPageContentType> = this.cmsFacade
    .getContentByName({
      pageName: 'bankwide-introduction-dialog-carousel',
      pageType: 'campaignbannermultiple',
      locale: 'en-AU',
    })
    .pipe(filter(isNotNull));

  /**
   * Read the live instance on every call. embla-carousel-angular >= 21 creates
   * `emblaApi` in `afterNextRender`, i.e. after this component's
   * `ngAfterViewInit`, so caching it there captured `undefined` for good.
   */
  scrollIndex() {
    return this.emblaRef?.emblaApi?.selectedScrollSnap() ?? 0;
  }

  nextSlideTitle(campaignBanner: CampaignBanner[]) {
    return campaignBanner?.[this.scrollIndex()]?.subContent;
  }

  isLastSlide(campaignBanner: CampaignBanner[]) {
    return this.scrollIndex() === campaignBanner?.length - 1;
  }

  onEmblaChange(): void {
    this.cdRef.markForCheck();
  }

  onPrev() {
    this.emblaRef.scrollPrev();
  }

  onNext() {
    this.emblaRef.scrollNext();
  }

  onLastSlideSelect() {
    this.userCustomizationsFacade.putUserCustomizations({
      bankWideLoyaltyPopupDisplayed: true,
    });
    this.dialogService.closeCurrentDialog();
  }

  trackByFn(index: number, { title }: CampaignBanner): string {
    return `${title}+${index}`;
  }

  closeDialog() {
    this.dialogService.closeCurrentDialog();
  }
}
